import React, { useState } from 'react'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import type { UserProfileResponse } from '@certi/shared/types'
import { useAuth } from '@/hooks/useAuth'

interface SecuritySectionProps {
  profile: UserProfileResponse
  onUpdate: (updatedProfile: UserProfileResponse) => void
}

export default function SecuritySection({ profile, onUpdate }: SecuritySectionProps) {
  const { accessToken, signOut } = useAuth()
  const [activeSection, setActiveSection] = useState<'email' | 'password' | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Email change state
  const [emailData, setEmailData] = useState({
    newEmail: '',
    confirmEmail: '',
  })

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!accessToken) return

    if (emailData.newEmail !== emailData.confirmEmail) {
      setError('Email addresses do not match')
      return
    }

    if (emailData.newEmail === profile.email) {
      setError('New email must be different from current email')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setSuccess(null)
      
      const updatedProfile = await UserAPI.updateEmail(
        profile.id,
        emailData.newEmail,
        accessToken
      )
      
      onUpdate(updatedProfile)
      setActiveSection(null)
      setEmailData({ newEmail: '', confirmEmail: '' })
      setSuccess('Email updated successfully')
    } catch (err) {
      console.error('Failed to update email:', err)
      setError(err instanceof Error ? err.message : 'Failed to update email')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!accessToken) return

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setSuccess(null)
      
      await UserAPI.updatePassword(
        profile.id,
        passwordData.newPassword,
        accessToken
      )
      
      setActiveSection(null)
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setSuccess('Password updated successfully. You will be logged out shortly.')
      
      // Log out user after password change for security
      setTimeout(() => {
        signOut()
      }, 2000)
    } catch (err) {
      console.error('Failed to update password:', err)
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setActiveSection(null)
    setEmailData({ newEmail: '', confirmEmail: '' })
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    setError(null)
    setSuccess(null)
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Email Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Email Address</h3>
            <p className="text-sm text-gray-600">Manage your account email address</p>
          </div>
          {activeSection !== 'email' && (
            <button
              onClick={() => setActiveSection('email')}
              className="text-brand-primary hover:text-brand-hover font-medium"
            >
              Change Email
            </button>
          )}
        </div>

        {activeSection === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Email
              </label>
              <input
                type="email"
                value={profile.email}
                disabled
                className="w-full input bg-gray-50 text-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Email Address
              </label>
              <input
                type="email"
                value={emailData.newEmail}
                onChange={(e) => setEmailData(prev => ({ ...prev, newEmail: e.target.value }))}
                className="w-full input"
                placeholder="Enter new email address"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Email
              </label>
              <input
                type="email"
                value={emailData.confirmEmail}
                onChange={(e) => setEmailData(prev => ({ ...prev, confirmEmail: e.target.value }))}
                className="w-full input"
                placeholder="Confirm new email address"
                required
              />
            </div>
            <div className="flex items-center justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Email'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <p className="text-gray-900 font-medium">{profile.email}</p>
            <p className="text-xs text-gray-500 mt-1">
              Your email is used for sign-in and important notifications
            </p>
          </div>
        )}
      </div>

      {/* Password Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Password</h3>
            <p className="text-sm text-gray-600">Update your account password</p>
          </div>
          {activeSection !== 'password' && (
            <button
              onClick={() => setActiveSection('password')}
              className="text-brand-primary hover:text-brand-hover font-medium"
            >
              Change Password
            </button>
          )}
        </div>

        {activeSection === 'password' ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full input"
                placeholder="Enter current password"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="w-full input"
                placeholder="Enter new password (min 8 characters)"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full input"
                placeholder="Confirm new password"
                required
              />
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-yellow-800 text-sm">
                <strong>Security Notice:</strong> You will be logged out after changing your password for security purposes.
              </p>
            </div>
            <div className="flex items-center justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <p className="text-gray-900 font-medium">••••••••••••</p>
            <p className="text-xs text-gray-500 mt-1">
              Last updated: Never
            </p>
          </div>
        )}
      </div>

      {/* Account Security Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-2">Account Security Tips</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use a strong, unique password for your account</li>
          <li>• Enable two-factor authentication when available</li>
          <li>• Never share your login credentials with others</li>
          <li>• Regularly review your account activity</li>
          <li>• Contact support if you notice any suspicious activity</li>
        </ul>
      </div>
    </div>
  )
}